import React, { useState, useEffect } from "react";
import { X, Utensils, Users } from "lucide-react";
import SearchableSelect from "./SearchableSelect";

export default function AddTableModal({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  isSubmitting = false,
}) {
  const [formData, setFormData] = useState({
    name: "",
    capacity: 4,
    status: "AVAILABLE",
    notes: "",
    reserved_by: "",
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        capacity: initialData.capacity || 4,
        status: initialData.status || "AVAILABLE",
        notes: initialData.notes || "",
        reserved_by: initialData.reserved_by || "",
      });
    } else {
      setFormData({
        name: "",
        capacity: 4,
        status: "AVAILABLE",
        notes: "",
        reserved_by: "",
      });
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSubmit(formData);
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden modal-panel-enter">

        {/* Header — matches AddSalesTypeModal teal bar */}
        <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <Utensils className="w-4.5 h-4.5 opacity-80" />
            <h2 className="text-base font-semibold tracking-tight">
              {initialData ? "Edit Data Meja" : "Tambah Meja Baru"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">

          {/* Nama Meja */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Nama / Nomor Meja <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Contoh: Meja 01, Meja A.09, VIP 1"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
            />
          </div>

          {/* Kapasitas */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Kapasitas (Orang)
            </label>
            <div className="relative">
              <input
                type="number"
                min="1"
                max="50"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value, 10) || 1 })}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
              />
              <Users className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            </div>
          </div>

          {/* Status Meja */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Status Meja
            </label>
            <SearchableSelect
              options={[
                { id: "AVAILABLE", name: "Kosong (Available)" },
                { id: "OCCUPIED", name: "Terisi (Occupied)" },
                { id: "RESERVED", name: "Reservasi (Reserved)" },
                { id: "INACTIVE", name: "Nonaktif (Inactive)" },
              ]}
              value={formData.status}
              onChange={(val) => setFormData({ ...formData, status: val })}
              placeholder="Pilih Status Meja..."
              searchable={false}
              size="md"
            />
          </div>

          {/* Atas Nama (RESERVED & OCCUPIED) */}
          {(formData.status === "RESERVED" || formData.status === "OCCUPIED") && (
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${
                formData.status === "RESERVED" ? "text-amber-700" : "text-rose-700"
              }`}>
                {formData.status === "RESERVED" ? "Atas Nama (Pemesan Reservasi)" : "Terisi oleh (Nama Pelanggan)"}
              </label>
              <input
                type="text"
                value={formData.reserved_by}
                onChange={(e) => setFormData({ ...formData, reserved_by: e.target.value })}
                placeholder={formData.status === "RESERVED" ? "Contoh: Rudi Setiawan / Ibu Rani" : "Contoh: Eva, Budi, dll"}
                className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent focus:bg-white transition-colors ${
                  formData.status === "RESERVED"
                    ? "bg-amber-50 border border-amber-200 text-amber-900 placeholder-amber-400 focus:ring-amber-500"
                    : "bg-rose-50 border border-rose-200 text-rose-900 placeholder-rose-400 focus:ring-rose-500"
                }`}
              />
            </div>
          )}

          {/* Catatan */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Catatan / Keterangan <span className="text-slate-400">(Opsional)</span>
            </label>
            <textarea
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Contoh: Kosong sejak 14:10 / Perbaikan meja"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 rounded-b-2xl flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors disabled:opacity-50">
            Batal
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm shadow-teal-600/20 disabled:opacity-50">
            {isSubmitting ? "Menyimpan..." : initialData ? "Simpan Perubahan" : "Tambah Meja"}
          </button>
        </div>
      </div>
    </div>
  );
}
